import toast from 'react-hot-toast';
import {
    SET_SEARCH_QUERY,
    SET_ASSIGNEE,
    SET_AUTHOR,
    SET_SELECTED_TAGS,
    TOGGLE_TAG,
    SET_TAG_METHOD,
    SET_BOOKMARKED_ONLY,
    SET_ANCESTOR_ID,
    SET_STATUS_FILTER,
    SET_CHANNEL_IDS,
    TOGGLE_CHANNEL_ID,
    TOGGLE_GROUP_CHANNELS,
    CLEAR_CHANNEL_IDS,
    SET_MEDIA_TYPE_FILTER,
    SET_SENT_BY_FILTER,
    SET_SENT_TO_FILTER,
    SET_DATE_RANGE_FILTER,
    CLEAR_ALL_FILTERS,
    SET_APPLIED_FILTERS,
    SET_SEARCH_RESULTS,
    UPDATE_FOLDER,
    DELETE_FOLDER,
    UPDATE_LINK,
    DELETE_LINK,
    SET_ANCESTOR_INFO,
    SET_USERS,
    SET_TAGS,
    SET_RECENT_TAGS,
    SET_FETCHING_NEW_DATA,
    SET_VIEW_MODE,
    SET_ACTIVE_TAB,
    SET_PAGE_SIZE,
    SET_PAGE,
    SET_FOLDERS_PAGE,
    SET_LINKS_PAGE,
    SET_MESSAGES_PAGE,
    SET_INITIALIZED
} from '../constants/search.constant';
import * as searchService from '../../services/search.service';

// ============ Simple Filter Actions ============

export const setSearchQuery = (query) => ({
    type: SET_SEARCH_QUERY,
    payload: query
});

export const setAssignee = (assigneeId) => ({
    type: SET_ASSIGNEE,
    payload: assigneeId
});

export const setAuthor = (authorId) => ({
    type: SET_AUTHOR,
    payload: authorId
});

export const setSelectedTags = (tagIds) => ({
    type: SET_SELECTED_TAGS,
    payload: tagIds
});

export const toggleTag = (tagId) => ({
    type: TOGGLE_TAG,
    payload: tagId
});

export const setTagMethod = (isOr) => ({
    type: SET_TAG_METHOD,
    payload: isOr
});

export const setBookmarkedOnly = (bookmarked) => ({
    type: SET_BOOKMARKED_ONLY,
    payload: bookmarked
});

export const setAncestorId = (ancestorId) => ({
    type: SET_ANCESTOR_ID,
    payload: ancestorId
});

export const setStatusFilter = (statusArray) => ({
    type: SET_STATUS_FILTER,
    payload: statusArray
});

export const setChannelIds = (ids) => ({
    type: SET_CHANNEL_IDS,
    payload: ids
});

export const toggleChannelId = (id) => ({
    type: TOGGLE_CHANNEL_ID,
    payload: id
});

export const toggleGroupChannels = (groupId, channelIds) => ({
    type: TOGGLE_GROUP_CHANNELS,
    payload: { groupId, channelIds }
});

export const clearChannelIds = () => ({
    type: CLEAR_CHANNEL_IDS
});

export const setMediaTypeFilter = (mediaType) => ({
    type: SET_MEDIA_TYPE_FILTER,
    payload: mediaType
});

export const setSentBy = (userId) => ({
    type: SET_SENT_BY_FILTER,
    payload: userId
});

export const setSentTo = (userId) => ({
    type: SET_SENT_TO_FILTER,
    payload: userId
});

export const setDateRangeFilter = (from, to) => ({
    type: SET_DATE_RANGE_FILTER,
    payload: { from, to }
});

export const clearAllFilters = () => ({
    type: CLEAR_ALL_FILTERS
});

// ============ UI Actions ============

export const setViewMode = (mode) => ({
    type: SET_VIEW_MODE,
    payload: mode
});

export const setActiveTab = (tab) => ({
    type: SET_ACTIVE_TAB,
    payload: tab
});

export const setPageSize = (size) => ({
    type: SET_PAGE_SIZE,
    payload: size
});

export const setPage = (page) => ({
    type: SET_PAGE,
    payload: page
});

export const setFoldersPage = (page) => ({
    type: SET_FOLDERS_PAGE,
    payload: page
});

export const setLinksPage = (page) => ({
    type: SET_LINKS_PAGE,
    payload: page
});

export const setMessagesPage = (page) => ({
    type: SET_MESSAGES_PAGE,
    payload: page
});

export const setInitialized = (initialized) => ({
    type: SET_INITIALIZED,
    payload: initialized
});

// ============ Results Actions ============

export const updateFolder = (id, updates) => ({
    type: UPDATE_FOLDER,
    payload: { id, updates }
});

export const deleteFolder = (id) => ({
    type: DELETE_FOLDER,
    payload: id
});

export const updateLink = (id, updates) => ({
    type: UPDATE_LINK,
    payload: { id, updates }
});

export const deleteLink = (id) => ({
    type: DELETE_LINK,
    payload: id
});

export const clearAncestorInfo = () => ({
    type: SET_ANCESTOR_INFO,
    payload: null
});

// ============ Thunk Actions (API calls) ============

/**
 * Fetch users and tags for filter dropdowns
 */
export const fetchFilterData = () => async (dispatch) => {
    try {
        const [usersAndTags, recentTagsResponse] = await Promise.all([
            searchService.fetchUsersAndTags(),
            searchService.fetchRecentTags().catch(() => ({ recent_tags: [] }))
        ]);

        dispatch({ type: SET_USERS, payload: usersAndTags.users || [] });
        dispatch({ type: SET_TAGS, payload: usersAndTags.tags || [] });
        dispatch({ type: SET_RECENT_TAGS, payload: recentTagsResponse.recent_tags || [] });
    } catch (error) {
        toast.error('Error fetching filter data.');
        console.error('Error fetching filter data:', error);
    }
};

/**
 * Determine optimal search type based on active filters
 * @param {Object} filters - Current filter state
 * @returns {string|null} - Search type or null for all types
 */
const getOptimalSearchType = (filters) => {
    // If only message-specific filters are set (channelIds, mediaType, sentBy, sentTo),
    // and no folder/link-applicable filters are set, search only messages
    // NOTE: dateFrom and dateTo are NOT message-only filters anymore - they apply to folders, links, and messages
    const hasMessageOnlyFilters = (filters.channelIds?.length > 0) || filters.mediaType || filters.sentBy ||
        filters.sentTo;
    const hasFolderLinkFilters = filters.assignee || filters.ancestorId ||
        filters.status.length > 0 || filters.selectedTags.length > 0 ||
        filters.bookmarkedOnly || filters.dateFrom || filters.dateTo;

    if (hasMessageOnlyFilters && !hasFolderLinkFilters && !filters.query) {
        return 'messages';
    }

    // Default: search all types
    return null;
};

/**
 * Execute search with current filters
 * @param {string} searchType - Optional: 'folders', 'links', 'messages', or comma-separated combination
 *                              If not provided, automatically determines optimal type based on filters
 */
export const performSearch = (searchType = 'auto') => async (dispatch, getState) => {
    const { search } = getState();
    const { filters } = search;

    // Don't search if no filters applied
    const hasFilters = filters.assignee || filters.author || filters.query ||
        filters.bookmarkedOnly || filters.ancestorId ||
        filters.status.length > 0 || filters.selectedTags.length > 0 ||
        (filters.channelIds?.length > 0) || filters.mediaType || filters.sentBy ||
        filters.sentTo || filters.dateFrom || filters.dateTo;

    if (!hasFilters) {
        // Only clear results if there's also no query text
        // (user might have typed a query but cleared all filters - keep results)
        if (!filters.query) {
            dispatch({
                type: SET_SEARCH_RESULTS,
                payload: { folders: [], links: [], messages: [], tasks: [] }
            });
        }
        dispatch({ type: SET_APPLIED_FILTERS, payload: { ...filters } });
        return;
    }

    dispatch({ type: SET_FETCHING_NEW_DATA, payload: true });
    dispatch({ type: SET_APPLIED_FILTERS, payload: { ...filters } });

    // Determine search type - 'auto' means smart detection, null means all types
    const effectiveSearchType = searchType === 'auto'
        ? getOptimalSearchType(filters)
        : searchType;

    try {
        const data = await searchService.executeSearch(filters, effectiveSearchType);

        const normalizedFolders = (data.folders || []).map(searchService.normalizeFolder);

        dispatch({
            type: SET_SEARCH_RESULTS,
            payload: {
                folders: normalizedFolders,
                links: data.links || [],
                messages: data.messages || [],
                tasks: data.tasks || []
            }
        });

        dispatch({ type: SET_ANCESTOR_INFO, payload: data.ancestor || null });
    } catch (error) {
        toast.error('Error fetching search results.');
        console.error('Error fetching search results:', error);
    } finally {
        dispatch({ type: SET_FETCHING_NEW_DATA, payload: false });
    }
};

/**
 * Search only folders (optimized - skips links and messages queries)
 */
export const searchFoldersOnly = () => performSearch('folders');

/**
 * Search only links (optimized - skips folders and messages queries)
 */
export const searchLinksOnly = () => performSearch('links');

/**
 * Search only messages (optimized - skips folders and links queries)
 */
export const searchMessagesOnly = () => performSearch('messages');

/**
 * Search folders and links only (skips messages query)
 */
export const searchFoldersAndLinks = () => performSearch('folders,links');

/**
 * Initialize search state from URL params
 */
export const initializeFromUrl = (searchParams) => (dispatch) => {
    const queryParam = searchParams.get('query') || '';
    const assignedToParam = searchParams.get('assigned_to');
    const authorParam = searchParams.get('created_by');
    const tagsParam = searchParams.get('tags');
    const tagMethodParam = searchParams.get('tag_method');
    const bookmarkedParam = searchParams.get('bookmarked_only');
    const ancestorIdParam = searchParams.get('ancestor_id');
    const statusParam = searchParams.get('status') || '';
    const channelIdsParam = searchParams.get('channel_ids');
    const channelIdParam = searchParams.get('channel_id'); // Legacy support
    const mediaTypeParam = searchParams.get('media_type');
    const sentByParam = searchParams.get('sent_by');
    const sentToParam = searchParams.get('sent_to');
    const dateFromParam = searchParams.get('date_from');
    const dateToParam = searchParams.get('date_to');

    if (queryParam) dispatch(setSearchQuery(queryParam));
    if (assignedToParam) dispatch(setAssignee(parseInt(assignedToParam)));
    if (authorParam) dispatch(setAuthor(parseInt(authorParam)));
    if (ancestorIdParam) dispatch(setAncestorId(parseInt(ancestorIdParam)));

    // Parse channel IDs (supports both channel_ids and legacy channel_id)
    let channelIds = [];
    if (channelIdsParam) {
        channelIds = channelIdsParam.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
    } else if (channelIdParam) {
        const id = parseInt(channelIdParam);
        if (!isNaN(id)) channelIds = [id];
    }
    if (channelIds.length > 0) dispatch(setChannelIds(channelIds));

    if (mediaTypeParam) dispatch(setMediaTypeFilter(mediaTypeParam));
    if (sentByParam) dispatch(setSentBy(parseInt(sentByParam)));
    if (sentToParam) dispatch(setSentTo(parseInt(sentToParam)));
    if (dateFromParam || dateToParam) dispatch(setDateRangeFilter(dateFromParam || null, dateToParam || null));

    if (statusParam) {
        const statusArray = statusParam.split(',').filter(s => s.trim() !== '');
        dispatch(setStatusFilter(statusArray));
    }

    if (tagsParam) {
        const tagIds = tagsParam.split(',').map(id => parseInt(id)).filter(id => !isNaN(id));
        dispatch(setSelectedTags(tagIds));
    }

    if (tagMethodParam === 'and') dispatch(setTagMethod(false));
    if (bookmarkedParam === 'true') dispatch(setBookmarkedOnly(true));

    // Set applied filters to match initial state
    const filters = {
        query: queryParam,
        assignee: assignedToParam ? parseInt(assignedToParam) : null,
        author: authorParam ? parseInt(authorParam) : null,
        selectedTags: tagsParam ? tagsParam.split(',').map(id => parseInt(id)).filter(id => !isNaN(id)) : [],
        tagMethodOr: tagMethodParam !== 'and',
        bookmarkedOnly: bookmarkedParam === 'true',
        ancestorId: ancestorIdParam ? parseInt(ancestorIdParam) : null,
        status: statusParam ? statusParam.split(',').filter(s => s.trim() !== '') : [],
        channelIds: channelIds,
        mediaType: mediaTypeParam || null,
        sentBy: sentByParam ? parseInt(sentByParam) : null,
        sentTo: sentToParam ? parseInt(sentToParam) : null,
        dateFrom: dateFromParam || null,
        dateTo: dateToParam || null
    };

    dispatch({ type: SET_APPLIED_FILTERS, payload: filters });
    dispatch(setInitialized(true));
};

/**
 * Toggle status in filter (add/remove from array)
 */
export const toggleStatusFilter = (statusItem) => (dispatch, getState) => {
    const { search } = getState();
    const currentStatus = search.filters.status;

    const newStatus = currentStatus.includes(statusItem)
        ? currentStatus.filter(s => s !== statusItem)
        : [...currentStatus, statusItem];

    dispatch(setStatusFilter(newStatus));
};

